import { TinyVariant } from '@shared/constants/components/tinyUtils';
import { McpServerModel } from '@shared/models/mcp/McpServerModel';
import type { TFunction } from 'i18next';

/**
 * How a configured server is used, flattening `enabled` + `activation` into one choice: Off (never
 * used), Auto (the assistant may load its tools), or Manual (only when attached with / or @).
 *
 * The skills equivalent is `skillMode.ts`; the two stay deliberately parallel so a tool and a skill
 * read the same way on a card and in a detail dialog.
 */
export type McpMode = 'off' | 'auto' | 'manual';

/** The single mode a server is currently in. */
export function mcpModeFor(server: McpServerModel): McpMode {
    return !server.enabled ? 'off' : server.activation;
}

/** Update payload for switching a server to `mode` — Off pauses it, the rest set the activation. */
export function mcpModeUpdate(serverId: string, mode: McpMode) {
    return mode === 'off' ? { id: serverId, enabled: false } : { id: serverId, enabled: true, activation: mode };
}

export interface McpModeOption {
    value: McpMode;
    label: string;
    description: string;
}

/** The three mode choices, in order, with the wording used by both the card tag and the toggle. */
export function mcpModeOptions(t: TFunction<'common'>): McpModeOption[] {
    return [
        {
            value: 'off',
            label: t('mcp.mode.off'),
            description: t('mcp.mode.offTooltip'),
        },
        {
            value: 'auto',
            label: t('mcp.activation.autoShort'),
            description: t('mcp.activation.autoDescription'),
        },
        {
            value: 'manual',
            label: t('mcp.activation.manualShort'),
            description: t('mcp.activation.manualDescription'),
        },
    ];
}

/** How the mode reads as a single tag on a card — the same colours skills use for the same states. */
export function mcpModeTagVariant(mode: McpMode): TinyVariant {
    if (mode === 'auto') return 'success';
    if (mode === 'manual') return 'accent';
    return 'diminished';
}
